import { arrayUnion, serverTimestamp, Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable } from "mobx";
import uuid from "react-native-uuid";
import { TKey } from "../../../shared/translations/translationKeys";
import type { BusinessEmailItemData } from "../../../shared/types";
import type { Api } from "../../core/apis";
import type { BaseModelData } from "../../core/BaseModel";
import { BaseModel } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { trackEvent } from "../../core/trackEvent";
import { isDefined } from "../../core/utils";
import { allRoles, Permission } from "../../permissions/types";
import type { PlanInfoProps } from "../../plans/PlanInfoBox";
import type PlanModel from "../../plans/PlanModel";
import { log } from "../../ui/log";
import type { EditorValue } from "../../ui/YooptaEditor";
import type UserModel from "../../users/UserModel";
import type { UserModelData, UserProfileData } from "../../users/UserModel";
import type {
  CustomerProfileData,
  CustomerProfileQuestion,
} from "../admin/customers/BusinessCustomerModel";
import type { BusinessTemplateScheduleItem } from "../admin/templates/BusinessTemplateModel";
import type { BusinessModuleTemplateData } from "../admin/templates/types";
import {
  BusinessFeedEventType,
  BusinessFeedItem,
  type BusinessFeedItemData,
  BusinessFeedMentionType,
} from "../feed/BusinessFeedItem";
import type { FeedItemUserIntroData } from "../feed/events/FeedItemUserIntro";
import { buildFeedMentionsFromText, getDisplayedRole } from "../feed/utils";
import type BusinessModuleModel from "../modules/BusinessModuleModel";
import type {
  BusinessModuleModelData,
  Snapshot,
} from "../modules/BusinessModuleModel";
import BusinessReminderModuleModel from "../modules/BusinessReminder/BusinessReminderModuleModel";
import { BusinessModuleState } from "../modules/types";

const MAX_FEED_IN_CACHE = 5;

export enum BusinessPlanMemberLevel {
  DEV = 1 << 0, // 1
  BusinessAdmin = 1 << 1, // 2
  BusinessGuide = 1 << 2, // 4
}

export type BusinessPlanSection = {
  uid: string;
  title: string;
  emoji: string | null;

  items: BusinessModuleTaskInfo[];
  isVisible: boolean; // Shown in UI or not
  isDeleted: boolean; // If not active anymore
};

export type BusinessStaticUserInfo = {
  // Ref to regular user, but with extended "static" info
  roles: string[]; // admin, guide etc
  title: string | null; // "Guide" etc
  displayName: string;
  email: string;
  avatarUrl: string | null;
  createdAt: Timestamp;
  profile: UserProfileData | null;
  isVisible: boolean; // Shown in UI or not
  isDeleted: boolean; // If not active anymore
  introDone: boolean; // Set when skipped or completed
  hasJoined: boolean; // Set when authed users are added to user

  tags: string[];
};

export type BusinessStaticUserInfoWithUserUid = BusinessStaticUserInfo & {
  userUid: string;
};

export type BusinessCustomerStaticInfo = CustomerProfileData & {
  uid: string;
};

export type BusinessModuleTaskInfo = {
  isVisible: boolean;
  isDeleted: boolean;
  businessModuleUid: string;
};

export type MentionLookup = {
  uid: string;
  type: BusinessFeedMentionType;
  description?: string;
};

export type MentionsLookup = Record<string, MentionLookup>;

export type BusinessTag = {
  uid: string;
  name: string;

  isDeleted: boolean;

  // memberLevel?
  // visible?
};

export type BusinessTagInfo = BusinessTag & { amount: number };

export type BusinessSettings = {
  useLocale: string | null;
  usePlanira: boolean;
  useIntroductionText: boolean;
};

export type BusinessPlanRole = {
  emoji: string | null;
  color: string;
  name: string;
  displayName: string;
  permissions: Permission[];
};

export type BusinessEventInfo = {
  date: Timestamp | null;
  tasks: BusinessModuleTaskInfo[];
  tags: BusinessTag[];
  settings: BusinessSettings | null;
};

export type TeamStaticInfo = {
  uid: string; // uuid
  title: string;
  userUids: string[];
  planUid?: string;
};

export enum FeedFilter {
  Everything = 1,
  Media = 2,
  Mentions,
}

export enum BusinessType {
  Demo = "demo",
  Regular = "regular",
}

export enum BusinessStatus {
  Active = "active",
  Archived = "archived",
}

export interface BusinessPlanModelData extends BaseModelData {
  type: BusinessType;
  status: BusinessStatus;
  customerInfo: BusinessCustomerStaticInfo;
  eventInfo: BusinessEventInfo;
  planInfo: PlanInfoProps; // Shared with sub planiras
  userInfo: { [key: string]: BusinessStaticUserInfo };
  sections: BusinessPlanSection[]; // Visible in menu
  profileQuestions: CustomerProfileQuestion[];
  feedCount: number;
  feedMediaCount: number;
  feedPinnedEvents: BusinessFeedItemData[]; // Any pinned items
  feedLatestEvents: BusinessFeedItemData[]; // Cache of feed events

  // Teams with optional planira info
  teamsInfo: TeamStaticInfo[];
}

export class BusinessPlanModel extends BaseModel<BusinessPlanModelData> {
  constructor(stores: Stores, api: Api, data: BusinessPlanModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      feedCount: data.feedCount ?? 0,
      feedMediaCount: data.feedMediaCount ?? 0,
      feedLatestEvents: data.feedLatestEvents ?? [],
      feedPinnedEvents: data.feedPinnedEvents ?? [],
      planirasInfo: data.teamsInfo ?? [],
      sections: data.sections ?? [],
      teamsInfo: data.teamsInfo ?? [],
      profileQuestions: data.profileQuestions ?? [],
      eventInfo:
        data.eventInfo ??
        ({
          tasks: [],
          tags: [],
          date: null,
          settings: null,
          roles: [],
        } as BusinessEventInfo),
      type: data.type ?? BusinessType.Regular,
      status: data.status ?? BusinessStatus.Active,
    });

    if (!this.data.eventInfo.tasks) {
      Object.assign(this.data, {
        eventInfo: { ...this.data.eventInfo, tasks: [] },
      });
    }

    if (!this.data.eventInfo.tags) {
      Object.assign(this.data, {
        eventInfo: { ...this.data.eventInfo, tags: [] },
      });
    }

    if (!this.data.eventInfo.settings) {
      Object.assign(this.data, {
        eventInfo: {
          ...this.data.eventInfo,
          settings: {
            useLocale: "en",
            usePlanira: false,
            useIntroductionText: true,
          },
        } as BusinessEventInfo,
      });
    }

    // Add pinned items as models
    const pinnedFeedItemModels = this.data.feedPinnedEvents.map((d) => {
      return new BusinessFeedItem(this.stores, this.api, d);
    });
    // Add cached items as models
    const feedItemModels = this.data.feedLatestEvents.map((d) => {
      return new BusinessFeedItem(this.stores, this.api, d);
    });
    this.stores.businessFeedItemsStore.load(
      feedItemModels.concat(pinnedFeedItemModels),
    );

    makeObservable(this);
  }

  @observable showUserInfoSheet: null | BusinessStaticUserInfoWithUserUid =
    null;

  @action setShowUserInfo(userUid: string | null) {
    if (userUid === null) {
      this.showUserInfoSheet = null;
    } else {
      this.showUserInfoSheet = { userUid, ...this.data.userInfo[userUid] };
    }
  }

  @action async updateStatus(status: BusinessStatus) {
    this.data.status = status;
    await this.api.businessPlansApi.update(this.uid, {
      status: this.data.status,
    });
  }

  @action async updateUserInfo(
    userUid: string,
    info: Partial<BusinessStaticUserInfo>,
  ) {
    this.data.userInfo[userUid] = { ...this.data.userInfo[userUid], ...info };
    await this.api.businessPlansApi.update(this.uid, {
      [`userInfo.${userUid}`]: this.data.userInfo[userUid],
    });
  }

  @action async updateEventInfo(info: Partial<BusinessEventInfo>) {
    this.data.eventInfo = { ...this.data.eventInfo, ...info };
    await this.api.businessPlansApi.update(this.uid, {
      eventInfo: this.data.eventInfo,
    });
  }

  @action async updatePlanInfo(info: Partial<PlanInfoProps>) {
    this.data.planInfo = { ...this.data.planInfo, ...info };
    await this.api.businessPlansApi.update(this.uid, {
      planInfo: this.data.planInfo,
    });
  }

  @action async updateCustomerInfo(info: Partial<CustomerProfileData>) {
    this.data.customerInfo = { ...this.data.customerInfo, ...info };
    await this.api.businessPlansApi.update(this.uid, {
      customerInfo: this.data.customerInfo,
    });
  }

  get isDemo() {
    return this.data.type === BusinessType.Demo;
  }

  async addEmailData({ item }: { item: BusinessEmailItemData }) {
    if (this.isDemo) {
      return;
    }

    const user = this.userInPlan;
    if (user) {
      // Add an entry to be sent out later on
      await this.api.businessEmailDataApi.add({
        createdAt: serverTimestamp() as Timestamp,
        business: {
          uid: this.data.uid,
          name: this.title,
        },
        item,
      });
      trackEvent("business_email_data_added");
    }
  }

  @computed get mentionsLookup() {
    const knownLookups: MentionsLookup = {};

    this.visibleUsersSorted.forEach((user) => {
      knownLookups[user.displayName] = {
        uid: user.userUid,
        type: BusinessFeedMentionType.User,
      };
    });

    this.allBusinessModules.forEach((m) => {
      if (m.data.title) {
        knownLookups[m.data.title] = {
          uid: m.uid,
          type: BusinessFeedMentionType.Task,
        };
      }
    });

    const scheduleItems =
      this.stores.businessScheduleStore.getByBusinessPlanUid(this.uid);
    scheduleItems.forEach((scheduleItem) => {
      knownLookups[scheduleItem.data.title] = {
        uid: scheduleItem.uid,
        type: BusinessFeedMentionType.Schedule,
      };
    });

    this.tagsUsedInfo.forEach((tagInfo) => {
      knownLookups[tagInfo.name] = {
        uid: tagInfo.uid,
        type: BusinessFeedMentionType.Group,
        description:
          tagInfo.amount === 1
            ? ` (${tagInfo.amount} member)`
            : ` (${tagInfo.amount} members)`,
      };
    });

    return knownLookups;
  }

  async addSection(item: Omit<BusinessPlanSection, "items" | "supported">) {
    const sectionData = {
      ...item,
      items: [],
    };
    this.data.sections.push(sectionData);
    await this.api.businessPlansApi.update(this.uid, {
      sections: this.data.sections,
    });

    return sectionData;
  }

  async toggleTag(userUid: string, tagUid: string) {
    if (!this.data.userInfo[userUid].tags) {
      this.data.userInfo[userUid].tags = [];
    }

    if (!this.data.userInfo[userUid].tags.find((t) => t === tagUid)) {
      this.data.userInfo[userUid].tags.push(tagUid);
    } else {
      this.data.userInfo[userUid].tags = this.data.userInfo[
        userUid
      ].tags.filter((tag) => tag !== tagUid);
    }

    await this.api.businessPlansApi.update(this.uid, {
      [`userInfo.${userUid}`]: this.data.userInfo[userUid],
    });
  }

  @computed get tagsUsedInfo() {
    const tagsUsed: Record<string, BusinessTagInfo> = {};

    this.visibleUsersSorted.forEach((user) => {
      if (user.tags && user.tags.length > 0) {
        user.tags.forEach((tagUid) => {
          if (!tagsUsed[tagUid]) {
            const addTag = this.data.eventInfo.tags.find(
              (t) => t.uid === tagUid,
            );
            if (addTag) {
              tagsUsed[tagUid] = { ...addTag, amount: 1 };
            }
          } else {
            tagsUsed[tagUid].amount++;
          }
        });
      }
    });

    return Array.from(Object.values(tagsUsed));
  }

  async addTag(item: BusinessTag) {
    if (!this.data.eventInfo.tags.find((t) => t.name === item.name)) {
      this.data.eventInfo.tags.push(item);
      await this.api.businessPlansApi.update(this.uid, {
        eventInfo: this.data.eventInfo,
      });
    }
  }

  async deleteTag(tag: BusinessTag) {
    const t = this.data.eventInfo.tags.find((t) => t.uid === tag.uid);
    if (t) {
      t.isDeleted = true;
      await this.api.businessPlansApi.update(this.uid, {
        eventInfo: this.data.eventInfo,
      });
    }
  }

  @action async updateTagsOrder(tags: BusinessTag[]) {
    this.data.eventInfo.tags = tags;
    await this.api.businessPlansApi.set(this);
  }

  @action updateFeedItemInCache(item: BusinessFeedItem) {
    const inCacheIndex = this.data.feedLatestEvents.findIndex(
      (f) => f.uid === item.uid,
    );
    if (inCacheIndex !== -1) {
      // Update in place
      this.data.feedLatestEvents[inCacheIndex] = {
        ...item.toFireStore(),
        uid: item.uid,
      };
    }

    const inPinnedIndex = this.data.feedPinnedEvents.findIndex(
      (f) => f.uid === item.uid,
    );
    if (inPinnedIndex !== -1) {
      // Update in place
      this.data.feedPinnedEvents[inPinnedIndex] = {
        ...item.toFireStore(),
        uid: item.uid,
      };
    }

    if (inCacheIndex !== -1 || inPinnedIndex !== -1)
      void this.api.businessPlansApi.update(this.uid, {
        feedLatestEvents: this.data.feedLatestEvents,
        feedPinnedEvents: this.data.feedPinnedEvents,
      });
  }

  @action async toggleFeedItemIsPinned(item: BusinessFeedItem) {
    if (this.data.feedPinnedEvents.find((t) => t.uid === item.uid)) {
      this.data.feedPinnedEvents = this.data.feedPinnedEvents.filter(
        (t) => t.uid !== item.uid,
      );
    } else {
      this.data.feedPinnedEvents.push(item.data);
    }

    await this.api.businessPlansApi.update(this.uid, {
      feedPinnedEvents: this.data.feedPinnedEvents,
    });
  }

  addToCacheWithoutSave(feedItem: BusinessFeedItem) {
    // Save a cache in plan
    this.data.feedLatestEvents.unshift({
      ...feedItem.toFireStore(),
      uid: feedItem.uid,
    });
    if (this.data.feedLatestEvents.length > MAX_FEED_IN_CACHE) {
      this.data.feedLatestEvents.pop();
    }
  }

  deleteFromCacheWithoutSave(feedItem: BusinessFeedItem) {
    this.data.feedLatestEvents = this.data.feedLatestEvents.filter(
      (f) => f.uid !== feedItem.uid,
    );
  }

  async addToFeed(
    item: Omit<
      BusinessFeedItemData,
      | "uid"
      | "comments"
      | "businessPlanUid"
      | "latestComments"
      | "reactions"
      | "mentions"
      | "mentionUids"
      | "edited"
      | "deleted"
      | "hidden"
      | "pinned"
      | "reported"
    >,
    skipTextCheck?: boolean,
  ) {
    // TODO - move text to base class, out of itemData?

    // @ts-expect-error
    if (!item.itemData?.text && !item.itemData?.mediaUrl && !skipTextCheck) {
      throw Error("No text or media found in feed item");
    }

    const feedItemData: Omit<BusinessFeedItemData, "uid"> = {
      ...item,
      ts: serverTimestamp() as Timestamp,
      comments: 0,
      latestComments: [],
      reactions: [],
      hidden: false,
      deleted: false,
      edited: false,
      pinned: false,
      reported: 0,
      // @ts-expect-error
      ...buildFeedMentionsFromText(item.itemData.text ?? "", this),
      businessPlanUid: this.data.uid,
    };

    const feedItem = await this.api.businessFeedItemsApi.add(feedItemData);

    this.addToCacheWithoutSave(feedItem);

    this.data.feedCount++;

    // @ts-expect-error
    if (item.itemData?.mediaUrl) {
      this.data.feedMediaCount++;
    }

    void this.api.businessPlansApi.update(this.uid, {
      feedLatestEvents: this.data.feedLatestEvents,
      feedCount: this.data.feedCount,
      feedMediaCount: this.data.feedMediaCount,
    });

    const user = this.userInPlan;
    if (user) {
      void this.addEmailData({
        item: {
          uid: feedItem.uid,
          ts: feedItem.data.ts ?? Timestamp.now(),
          author: {
            uid: user.uid,
            name: user.data.displayName,
          },
          type: "feed",
          textSID: TKey.BusinessFeedItemPosted,
          textData: { comment: feedItem.textRepresentationInEmail },
        },
      });
    }

    return feedItem;
  }

  @computed get allInfoModules() {
    const items: BusinessModuleTaskInfo[] = [];

    for (const section of this.data.sections) {
      items.push(...section.items.filter((i) => i.isVisible));
    }

    return items;
  }

  getSectionByInfoItem(itemId: string) {
    for (const section of this.data.sections) {
      if (section.items.find((i) => i.businessModuleUid === itemId)) {
        return section;
      }
    }
  }

  getSection(sectionId: string) {
    return this.data.sections.find((d) => d.uid === sectionId);
  }

  @action async updateEventSettings(settings: Partial<BusinessSettings>) {
    if (this.data.eventInfo.settings) {
      this.data.eventInfo.settings = {
        ...this.data.eventInfo.settings,
        ...settings,
      };
      await this.api.businessPlansApi.update(this.uid, {
        eventInfo: this.data.eventInfo,
      });
    }
  }

  @action async addSectionModule(
    sectionUid: string,
    businessModuleUid: string,
  ) {
    const s = this.getSection(sectionUid);
    if (s) {
      s.items.push({ businessModuleUid, isVisible: true, isDeleted: false });
      await this.api.businessPlansApi.update(this.uid, {
        sections: this.data.sections,
      });
    }
  }

  @action async addEventModule(businessModuleUid: string) {
    if (!this.data.eventInfo.tasks) {
      this.data.eventInfo.tasks = [];
    }
    this.data.eventInfo.tasks.push({
      businessModuleUid,
      isDeleted: false,
      isVisible: true,
    });
    await this.api.businessPlansApi.update(this.uid, {
      eventInfo: this.data.eventInfo,
    });
  }

  @action
  async deleteBusinessModule(uid: string) {
    const m = this.data.eventInfo.tasks.find(
      (t) => t.businessModuleUid === uid,
    );
    if (m) {
      m.isDeleted = true;
      m.isVisible = false;
      await this.api.businessPlansApi.update(this.uid, {
        eventInfo: this.data.eventInfo,
      });
    }
  }

  @action
  async deleteBusinessSection(uid: string) {
    const s = this.data.sections.find((t) => t.uid === uid);
    if (s) {
      s.isDeleted = true;
      s.isVisible = false;
      await this.api.businessPlansApi.update(this.uid, {
        sections: this.data.sections,
      });
    }
  }

  async addScheduleItem(data: Omit<BusinessTemplateScheduleItem, "uid">) {
    await this.api.businessScheduleItemApi.add({
      businessPlanUid: this.uid,
      ...data,
    });
  }

  async addBusinessModule(data: Omit<BusinessModuleTemplateData, "uid">) {
    const businessModule = await this.api.businessModulesApi.add({
      ts: serverTimestamp() as Timestamp,
      state: BusinessModuleState.Planning,
      businessPlanUid: this.uid,
      feedItemUid: "",
      customerUid: this.data.customerInfo.uid,
      lastActivity: serverTimestamp() as Timestamp,
      snapshot: {},
      isDraggable: true,
      completedBy: [],
      excludedUsers: [],
      ...data,
    });

    // Update snapshot at once
    businessModule.updateSnapshotWithoutSave();
    await this.api.businessModulesApi.set(businessModule);

    return businessModule;
  }

  @computed get activeSections(): BusinessPlanSection[] {
    return this.data.sections.filter((s) => s.isVisible && !s.isDeleted);
  }

  @action async updateSectionOrder(sections: BusinessPlanSection[]) {
    this.data.sections = sections;
    await this.api.businessPlansApi.set(this);
  }

  async updateSection(section: BusinessPlanSection) {
    const s = this.data.sections.findIndex((s) => s.uid === section.uid);
    if (s !== undefined) {
      this.data.sections[s] = section;
      await this.api.businessPlansApi.set(this);
    }
  }

  // Showing not visible as well
  @computed get allUsersSorted(): BusinessStaticUserInfoWithUserUid[] {
    return Object.entries(this.data.userInfo)
      .map(([userUid, userInfo]) => {
        const uInfo = {
          ...userInfo,
          userUid,
        };

        if (!uInfo.roles) {
          uInfo.roles = []; // safe to remove later on
        }

        return uInfo;
      })
      .sort((a, b) => {
        const aRole = getDisplayedRole(a);
        const bRole = getDisplayedRole(b);

        // Users with roles come before users without roles.
        if (aRole && bRole) {
          return aRole?.name.localeCompare(bRole.name);
        }

        if (aRole && !bRole) {
          return -1;
        }

        if (!aRole && bRole) {
          return 1;
        }

        // This handles users with no roles AND provides a stable sort for users in the same group.
        return a.displayName.localeCompare(b.displayName);
      });
  }

  @computed get visibleUsersSorted(): BusinessStaticUserInfoWithUserUid[] {
    return this.allUsersSorted.filter((m) => m.isVisible);
  }

  @computed get allBusinessModules() {
    const modules: BusinessModuleModel<
      Snapshot,
      BusinessModuleModelData<Snapshot>
    >[] = [];
    this.data.eventInfo.tasks.forEach(({ businessModuleUid, isVisible }) => {
      const m = this.stores.businessModulesStore.byId.get(businessModuleUid);
      if (m && isVisible) {
        modules.push(m);
      }
    });

    return modules;
  }

  @computed get notCompletedBusinessModules() {
    const modules: BusinessModuleModel<
      Snapshot,
      BusinessModuleModelData<Snapshot>
    >[] = [];

    this.data.eventInfo.tasks.forEach(({ businessModuleUid, isVisible }) => {
      const m = this.stores.businessModulesStore.byId.get(businessModuleUid);
      if (m && !m.isCompletedByUserOrTeam && isVisible) {
        modules.push(m);
      }
    });

    return modules.sort((a, b) => {
      if (
        BusinessReminderModuleModel.is(a) &&
        BusinessReminderModuleModel.is(b) &&
        a.calculatedDateWithoutDefault &&
        b.calculatedDateWithoutDefault
      ) {
        return a.calculatedDateWithoutDefault < b.calculatedDateWithoutDefault
          ? -1
          : 1;
      }

      return 0;
    });
  }

  @computed get completedBusinessModules() {
    const modules: BusinessModuleModel<
      Snapshot,
      BusinessModuleModelData<Snapshot>
    >[] = [];

    this.data.eventInfo.tasks.forEach(({ businessModuleUid, isVisible }) => {
      const m = this.stores.businessModulesStore.byId.get(businessModuleUid);
      if (m?.isCompletedByUserOrTeam && isVisible) {
        modules.push(m);
      }
    });

    return modules.sort((a, b) => {
      if (
        BusinessReminderModuleModel.is(a) &&
        BusinessReminderModuleModel.is(b) &&
        a.calculatedDateWithoutDefault &&
        b.calculatedDateWithoutDefault
      ) {
        return a.calculatedDateWithoutDefault < b.calculatedDateWithoutDefault
          ? -1
          : 1;
      }

      return 0;
    });
  }

  // Add more models as needed
  @computed get scheduleBusinessModules(): BusinessReminderModuleModel[] {
    const dueDateModuels = this.businessModules.filter(
      (module): module is BusinessReminderModuleModel =>
        module instanceof BusinessReminderModuleModel,
    );

    return dueDateModuels;
  }

  @computed get businessModules() {
    return [
      ...this.completedBusinessModules,
      ...this.notCompletedBusinessModules,
    ];
  }

  @computed get completedTasksCount(): number {
    return this.businessModules.filter((b) => b.isCompletedByUserOrTeam).length;
  }

  @computed get totalTasksCount(): number {
    return this.data.eventInfo.tasks.filter((t) => t.isVisible).length;
  }

  @computed get usersCount(): number {
    return this.visibleUsersSorted.length;
  }

  @computed get theme() {
    return this.data.customerInfo.theme;
  }

  @computed get introTextFromCustomer(): EditorValue | null {
    if (!this.data.eventInfo.settings?.useIntroductionText) {
      return null;
    }

    const introText = this.data.customerInfo.introText;

    if (!introText || !this.userInPlan) {
      return null;
    }

    const replacements: Record<string, string> = {
      displayName: this.userInPlan.data.displayName,
      eventName: this.title,
    };

    return JSON.parse(
      JSON.stringify(introText).replace(
        /@(\w+)@/g,
        (_, key) => replacements[key] ?? `@${key}@`,
      ),
    );
  }

  @computed get planUsersSorted(): BusinessStaticUserInfoWithUserUid[] {
    return this.visibleUsersSorted;
  }

  @computed get hasAdminAccess(): boolean {
    if (!this.userInPlan) {
      return false;
    }

    // TODO - make hasAdminAccess more granular
    return this.checkPermission(Permission.AccessToEverything);
  }

  @computed get isVisibleAsMember(): boolean {
    if (!this.userInPlan) {
      return false;
    }
    return this.data.userInfo[this.userInPlan.uid].isVisible;
  }

  @action async toggleMemberRole(userUid: string, role: string) {
    if (!allRoles.find((r) => r.name === role)) {
      throw Error(`Role ${role} not existing`);
    }

    let currentRoles = this.data.userInfo[userUid]?.roles ?? [];

    if (currentRoles.includes(role)) {
      currentRoles = currentRoles.filter((r) => r !== role);
    } else {
      currentRoles.push(role);
    }

    // Update the user's member level
    this.data.userInfo[userUid] = {
      ...this.data.userInfo[userUid],
      roles: currentRoles,
    };

    await this.api.businessPlansApi.update(this.uid, {
      [`userInfo.${userUid}`]: this.data.userInfo[userUid],
    });
  }

  @action async toggleIsVisibleAsMember(userUid?: string) {
    if (userUid) {
      const data = this.data.userInfo[userUid];
      if (data) {
        data.isVisible = !data.isVisible;
        await this.api.businessPlansApi.update(this.uid, {
          [`userInfo.${userUid}`]: this.data.userInfo[userUid],
        });
      }
    }
  }

  @action async toggleIsDeleted(userUid?: string) {
    if (userUid) {
      const data = this.data.userInfo[userUid];
      if (data) {
        data.isDeleted = !data.isDeleted;
        await this.api.businessPlansApi.update(this.uid, {
          [`userInfo.${userUid}`]: this.data.userInfo[userUid],
        });
      }
    }
  }

  @action
  async joinWithEmail(email: string) {
    const _currentUser = this.stores.sessionStore.uid;
    const { avatarUrl } = this.stores.sessionStore.superUser?.data ?? {
      avatarUrl: "",
    };

    const foundUser = await this.api.usersApi.getBusinessUserByEmail(
      email.trim().toLowerCase(),
      this.uid,
    );

    if (!foundUser) {
      throw new Error("NOT_FOUND");
    }

    await this.setAsJoined(foundUser.uid);

    return this.api.usersApi.update(foundUser.uid, {
      avatarUrl,
      authedUsers: arrayUnion(this.stores.sessionStore.uid),
    });
  }

  @action async setAsJoined(userUid: string) {
    if (!this.data.userInfo[userUid].hasJoined) {
      this.data.userInfo[userUid].hasJoined = true;
      await this.api.businessPlansApi.update(this.uid, {
        [`userInfo.${userUid}`]: this.data.userInfo[userUid],
      });
    }
  }

  @action
  async joinWithCurrentSuperUser() {
    // Get defaults from superUser
    const { avatarUrl, email, displayName } = this.stores.sessionStore.superUser
      ?.data ?? {
      avatarUrl: "",
      email: null,
      displayName: "Member",
    };

    return this.addUser({
      displayName,
      avatarUrl,
      email,
      authedUsers: [this.stores.sessionStore.uid],
    });
  }

  @action
  async addUser(props: {
    avatarUrl: string;
    displayName: string;
    email: string;
    authedUsers: string[];
  }) {
    if (!props.email.trim()) {
      throw Error("No email provided");
    }

    const foundUser = await this.api.usersApi.getBusinessUserByEmail(
      props.email.trim(),
      this.uid,
    );

    if (foundUser) {
      throw Error("Found user with this email already.");
    }

    // Create a user
    const userData: Omit<UserModelData, "uid"> = {
      ts: serverTimestamp() as Timestamp,
      avatarUrl: props.avatarUrl,
      moduleVisits: {},
      planUid: "", // Personal Planira
      businessPlanUid: this.uid,
      lastActivity: serverTimestamp() as Timestamp,
      authedUsers: props.authedUsers,
      displayName: props.displayName.trim(),
      profile: null,
      email: props.email,
      tokens: [],
    };

    const user = await this.api.usersApi.add(userData);

    // Add user to plan
    this.data.userInfo[user.uid] = {
      roles: [],
      title: null,
      displayName: props.displayName,
      avatarUrl: props.avatarUrl,
      email: props.email,
      createdAt: Timestamp.now(),
      profile: null,
      isVisible: true,
      isDeleted: false,
      introDone: false,
      hasJoined: false,
      tags: [],
    };
    await this.api.businessPlansApi.set(this);

    log.dev("User added to plan ", this.uid, { user });

    return user;
  }

  @computed get userInPlan(): UserModel | undefined {
    const uid = this.stores.sessionStore.uid;

    for (const user of this.usersWithData) {
      if (user.data.authedUsers.includes(uid)) {
        return user;
      }
    }

    return undefined;
  }

  @computed get hasCompletedIntroduction(): boolean {
    const user = this.userInPlan;
    if (!user) {
      return true;
    }

    const userInfo = this.data.userInfo[user.uid];

    if (!userInfo.isVisible) {
      return true;
    }

    return userInfo.introDone;
  }

  @action async setIntroductionAsCompleted() {
    const user = this.userInPlan;
    if (user && !this.data.userInfo[user.uid].introDone) {
      this.data.userInfo[user.uid].introDone = true;
      await this.api.businessPlansApi.update(this.uid, {
        userInfo: this.data.userInfo,
        [`userInfo.${user.uid}`]: this.data.userInfo[user.uid],
      });

      // Add feed event if visible user
      if (this.isVisibleAsMember) {
        void this.addToFeed(
          {
            type: BusinessFeedEventType.UserCompletedIntro,
            moduleUid: "",
            itemData: {
              authorUid: user.uid,
            } satisfies FeedItemUserIntroData,
          },
          true,
        );
      }
    }
  }

  @action async addNewTeam() {
    const user = this.userInPlan;
    if (!user) {
      throw Error("no user found");
    }

    // Create a new team
    this.data.teamsInfo.push({
      uid: uuid.v4(),
      title: this.title,
      userUids: [user.uid],
    });

    await this.api.businessPlansApi.update(this.uid, {
      teamsInfo: this.data.teamsInfo,
    });
  }

  @action async joinTeam(teamUid: string) {
    const user = this.userInPlan;
    if (!user) {
      throw Error("no user found");
    }

    const teamFound = this.data.teamsInfo.find((p) => p.uid === teamUid);

    if (teamFound) {
      // TODO - remove from the rest?
      teamFound.userUids.push(user.uid);
      await this.api.businessPlansApi.update(this.uid, {
        teamsInfo: this.data.teamsInfo,
      });
    }
  }

  @action async leaveTeam(teamUid: string) {
    const user = this.userInPlan;
    if (!user) {
      throw Error("no user found");
    }

    const teamFound = this.data.teamsInfo.find((p) => p.uid === teamUid);

    if (teamFound) {
      teamFound.userUids = teamFound.userUids.filter(
        (userUid) => userUid !== user.uid,
      );

      // leave any planira as well
      if (teamFound.planUid) {
        await user.setPlanUid("");
        await this.planira?.removeUserFromApprovalList();
      }

      await this.api.businessPlansApi.update(this.uid, {
        teamsInfo: this.data.teamsInfo,
      });
    }
  }

  @action async addNewPlanira(plan: PlanModel) {
    const user = this.userInPlan;
    if (!user) {
      throw Error("no user found");
    }

    await user.setPlanUid(plan.uid);

    // Add to regular planira as well
    await plan.addUserIdToPlan(user.uid);

    const currentTeam = this.currentTeam;
    if (currentTeam) {
      if (currentTeam.planUid) {
        throw Error("Team already had a plan set");
      }

      currentTeam.planUid = plan.uid;
    } else {
      // Set as a new team + planira
      this.data.teamsInfo.push({
        uid: uuid.v4(),
        planUid: plan.uid,
        title: plan.data.title,
        userUids: [user.uid],
      });
    }

    await this.api.businessPlansApi.update(this.uid, {
      teamsInfo: this.data.teamsInfo,
    });
  }

  @action async setType(type: BusinessType) {
    this.data.type = type;
    await this.api.businessPlansApi.update(this.uid, {
      type: this.data.type,
    });
  }

  @computed get planIds(): string[] {
    return this.data.teamsInfo.map((pInfo) => pInfo.planUid).filter(isDefined);
  }

  @computed get currentTeam(): TeamStaticInfo | undefined {
    if (!this.data.teamsInfo) {
      return undefined;
    }

    const user = this.userInPlan;
    return user
      ? this.data.teamsInfo.find((p) => p.userUids.includes(user.uid))
      : undefined;
  }

  @action async updateTeamTitleFromPlan(planUid: string, title: string) {
    const teamFound = this.data.teamsInfo.find((p) => p.planUid === planUid);

    if (teamFound) {
      teamFound.title = title;
      await this.api.businessPlansApi.update(this.uid, {
        teamsInfo: this.data.teamsInfo,
      });
    }
  }

  @action async updateTeamTitle(teamUid: string, title: string) {
    const teamFound = this.data.teamsInfo.find((p) => p.uid === teamUid);

    if (teamFound) {
      teamFound.title = title;
      await this.api.businessPlansApi.update(this.uid, {
        teamsInfo: this.data.teamsInfo,
      });
    }
  }

  @action async addUserToPlanira(plan: PlanModel, userUid: string) {
    const planFound = this.data.teamsInfo.find((p) => p.planUid === plan.uid);

    if (planFound) {
      planFound.userUids.push(userUid);
      await this.api.businessPlansApi.update(this.uid, {
        teamsInfo: this.data.teamsInfo,
      });
    }
  }

  @computed get usersWithData(): UserModel[] {
    const u: UserModel[] = [];
    for (const userId of Object.keys(this.data.userInfo)) {
      const uModel = this.stores.usersStore.byId.get(userId);
      if (uModel) {
        u.push(uModel);
      }
    }
    return u;
  }

  @computed get planira(): PlanModel | undefined {
    const planUid = this.userInPlan?.data.planUid;

    if (planUid) {
      return this.stores.plansStore.byId.get(planUid);
    }

    return undefined;
  }

  checkPermission(permission: Permission, forUserUid?: string): boolean {
    try {
      const userInfo = this.data.userInfo[forUserUid ?? this.userUidInPlan];

      if (!userInfo || !userInfo.roles || !userInfo.roles.length) {
        return false; // Not logged in or no roles
      }

      // Check if ANY of the user's roles grants this permission
      return userInfo.roles.some((role: string) => {
        const permissionsForRole: Permission[] =
          allRoles.find((r) => r.name === role)?.permissions ?? [];
        return (
          permissionsForRole?.includes(permission) ||
          permissionsForRole.includes(Permission.AccessToEverything)
        );
      });
    } catch {
      return false;
    }
  }

  @computed get planiras(): PlanModel[] {
    const allPlaniras: PlanModel[] = [];

    this.planIds.forEach((id) => {
      const plan = this.stores.plansStore.byId.get(id);
      if (plan) {
        allPlaniras.push(plan);
      }
    });

    return allPlaniras;
  }

  @computed get title() {
    return this.data.planInfo.title;
  }

  @computed get hasMoreFeedItems(): boolean {
    if (this.feedFilter === FeedFilter.Media) {
      return this.feedItems.length < this.data.feedMediaCount;
    }

    return (
      this.feedItems.length + this.data.feedPinnedEvents.length <
      this.data.feedCount
    );
  }

  @observable feedFilter: FeedFilter = FeedFilter.Everything;

  @action setFeedFilter(feedFilter: FeedFilter) {
    this.feedFilter =
      this.feedFilter === feedFilter ? FeedFilter.Everything : feedFilter;
  }

  @computed get userUidInPlan(): string {
    const u = this.userInPlan;

    if (!u) {
      throw Error("No user in plan");
    }

    return u.uid;
  }

  @computed get feedPinnedItems(): BusinessFeedItem[] {
    const items: BusinessFeedItem[] = [];

    for (const d of this.data.feedPinnedEvents) {
      const i = this.stores.businessFeedItemsStore.byId.get(d.uid);
      if (i) {
        items.push(i);
      }
    }

    return items.sort((a, b) =>
      (a.data.ts?.seconds ?? 0) > (b.data.ts?.seconds ?? 0) ? -1 : 1,
    );
  }

  @computed get feedItems(): BusinessFeedItem[] {
    const items: BusinessFeedItem[] = [];

    const data = this.stores.businessFeedItemsStore.all;
    for (const d of data) {
      if (d.data.businessPlanUid === this.uid) {
        if (
          this.feedFilter === FeedFilter.Media &&
          // @ts-expect-error
          !d.data.itemData.mediaUrl
        ) {
          continue;
        } else if (
          this.feedFilter === FeedFilter.Mentions &&
          !d.data.mentionUids.includes(this.userUidInPlan)
        ) {
          continue;
        }

        // Skip pinned events
        if (!this.data.feedPinnedEvents.some((f) => f.uid === d.uid)) {
          items.push(d);
        }
      }
    }

    return items.sort((a, b) =>
      (a.data.ts?.seconds ?? 0) > (b.data.ts?.seconds ?? 0) ? -1 : 1,
    );
  }

  @computed get mediaFeedItems(): BusinessFeedItem[] {
    // @ts-expect-error - todo, map up items
    return this.feedItems.filter((i) => !!i.data.itemData.mediaUrl);
  }

  toFireStore(): Omit<BusinessPlanModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
